/**
 * List of currency codes supported by Ankor Pay.
 */
export enum CurrencyCode {
  // United Arab Emirates Dirham
  AED = 'AED',
  // Australian Dollar
  AUD = 'AUD',
  // Bulgarian Lev
  BGN = 'BGN',
  // Brazilian Real
  BRL = 'BRL',
  // Canadian Dollar
  CAD = 'CAD',
  // Swiss Franc
  CHF = 'CHF',
  // Chinese Yuan
  CNY = 'CNY',
  // Czech Koruna
  CZK = 'CZK',
  // Danish Krone
  DKK = 'DKK',
  // Euro
  EUR = 'EUR',
  // British Pound
  GBP = 'GBP',
  // Hong Kong Dollar
  HKD = 'HKD',
  // Croatian Kuna
  HRK = 'HRK',
  // Hungarian Forint
  HUF = 'HUF',
  // Israeli New Shekel
  ILS = 'ILS',
  // Icelandic Króna
  ISK = 'ISK',
  // Indonesian Rupiah
  IDR = 'IDR',
  // Japanese Yen
  JPY = 'JPY',
  // South Korean Won
  KRW = 'KRW',
  // Sri Lankan Rupee
  LKR = 'LKR',
  // Moroccan Dirham
  MAD = 'MAD',
  // Malaysian Ringgit
  MYR = 'MYR',
  // Mexican Peso
  MXN = 'MXN',
  // Norwegian Krone
  NOK = 'NOK',
  // New Zealand Dollar
  NZD = 'NZD',
  // Philippine Peso
  PHP = 'PHP',
  // Polish Złoty
  PLN = 'PLN',
  // Romanian Leu
  RON = 'RON',
  // Swedish Krona
  SEK = 'SEK',
  // Singapore Dollar
  SGD = 'SGD',
  // Thai Baht
  THB = 'THB',
  // Turkish Lira
  TRY = 'TRY',
  // Tunisian Dinar
  TND = 'TND',
  // United States Dollar
  USD = 'USD',
  // Vietnamese Dong
  VND = 'VND',
  // South African Rand
  ZAR = 'ZAR',
}

export type CurrencyDescription = {
  code: CurrencyCode
  description: string
  symbol: string
  flag?: string // Optional flag for the currency - In cases for european currencies, we use the Euro flag - default to country flag if none here
}

export const currencyList: Record<CurrencyCode, CurrencyDescription> = {
  [CurrencyCode.AED]: {
    code: CurrencyCode.AED,
    description: 'United Arab Emirates Dirham',
    symbol: 'AED',
  },
  [CurrencyCode.AUD]: {
    code: CurrencyCode.AUD,
    description: 'Australian Dollar',
    symbol: '$',
  },
  [CurrencyCode.BGN]: {
    code: CurrencyCode.BGN,
    description: 'Bulgarian Lev',
    symbol: 'лв',
  },
  [CurrencyCode.BRL]: {
    code: CurrencyCode.BRL,
    description: 'Brazilian Real',
    symbol: 'R$',
  },
  [CurrencyCode.CAD]: {
    code: CurrencyCode.CAD,
    description: 'Canadian Dollar',
    symbol: 'CA$',
  },
  [CurrencyCode.CHF]: {
    code: CurrencyCode.CHF,
    description: 'Swiss Franc',
    symbol: 'CHF',
  },
  [CurrencyCode.CNY]: {
    code: CurrencyCode.CNY,
    description: 'Chinese Yuan',
    symbol: '¥',
  },
  [CurrencyCode.CZK]: {
    code: CurrencyCode.CZK,
    description: 'Czech Koruna',
    symbol: 'Kč',
  },
  [CurrencyCode.DKK]: {
    code: CurrencyCode.DKK,
    description: 'Danish Krone',
    symbol: 'kr',
  },
  [CurrencyCode.EUR]: {
    code: CurrencyCode.EUR,
    description: 'Euro',
    symbol: '€',
    flag: '🇪🇺',
  },
  [CurrencyCode.GBP]: {
    code: CurrencyCode.GBP,
    description: 'British Pound',
    symbol: '£',
  },
  [CurrencyCode.HKD]: {
    code: CurrencyCode.HKD,
    description: 'Hong Kong Dollar',
    symbol: 'HK$',
  },
  [CurrencyCode.HRK]: {
    code: CurrencyCode.HRK,
    description: 'Croatian Kuna',
    symbol: 'kn',
  },
  [CurrencyCode.HUF]: {
    code: CurrencyCode.HUF,
    description: 'Hungarian Forint',
    symbol: 'Ft',
  },
  [CurrencyCode.IDR]: {
    code: CurrencyCode.IDR,
    description: 'Indonesian Rupiah',
    symbol: 'Rp',
  },
  [CurrencyCode.ILS]: {
    code: CurrencyCode.ILS,
    description: 'Israeli New Shekel',
    symbol: '₪',
  },
  [CurrencyCode.ISK]: {
    code: CurrencyCode.ISK,
    description: 'Icelandic Króna',
    symbol: 'kr',
  },
  [CurrencyCode.JPY]: {
    code: CurrencyCode.JPY,
    description: 'Japanese Yen',
    symbol: '¥',
  },
  [CurrencyCode.KRW]: {
    code: CurrencyCode.KRW,
    description: 'South Korean Won',
    symbol: '₩',
  },
  [CurrencyCode.LKR]: {
    code: CurrencyCode.LKR,
    description: 'Sri Lankan Rupee',
    symbol: 'Rs',
  },
  [CurrencyCode.MAD]: {
    code: CurrencyCode.MAD,
    description: 'Moroccan Dirham',
    symbol: 'د.م.',
  },
  [CurrencyCode.MYR]: {
    code: CurrencyCode.MYR,
    description: 'Malaysian Ringgit',
    symbol: 'RM',
  },
  [CurrencyCode.MXN]: {
    code: CurrencyCode.MXN,
    description: 'Mexican Peso',
    symbol: 'MX$',
  },
  [CurrencyCode.NOK]: {
    code: CurrencyCode.NOK,
    description: 'Norwegian Krone',
    symbol: 'kr',
  },
  [CurrencyCode.NZD]: {
    code: CurrencyCode.NZD,
    description: 'New Zealand Dollar',
    symbol: 'NZ$',
  },
  [CurrencyCode.PHP]: {
    code: CurrencyCode.PHP,
    description: 'Philippine Peso',
    symbol: '₱',
  },
  [CurrencyCode.PLN]: {
    code: CurrencyCode.PLN,
    description: 'Polish Złoty',
    symbol: 'zł',
  },
  [CurrencyCode.RON]: {
    code: CurrencyCode.RON,
    description: 'Romanian Leu',
    symbol: 'lei',
  },
  [CurrencyCode.SEK]: {
    code: CurrencyCode.SEK,
    description: 'Swedish Krona',
    symbol: 'kr',
  },
  [CurrencyCode.SGD]: {
    code: CurrencyCode.SGD,
    description: 'Singapore Dollar',
    symbol: 'S$',
  },
  [CurrencyCode.THB]: {
    code: CurrencyCode.THB,
    description: 'Thai Baht',
    symbol: '฿',
  },
  [CurrencyCode.TND]: {
    code: CurrencyCode.TND,
    description: 'Tunisian Dinar',
    symbol: 'د.ت',
  },
  [CurrencyCode.TRY]: {
    code: CurrencyCode.TRY,
    description: 'Turkish Lira',
    symbol: '₺',
  },
  [CurrencyCode.USD]: {
    code: CurrencyCode.USD,
    description: 'United States Dollar',
    symbol: '$',
  },
  [CurrencyCode.VND]: {
    code: CurrencyCode.VND,
    description: 'Vietnamese Dong',
    symbol: '₫',
  },
  [CurrencyCode.ZAR]: {
    code: CurrencyCode.ZAR,
    description: 'South African Rand',
    symbol: 'R',
  },
}

export const conversionsCurrencies: Partial<Record<CurrencyCode, CurrencyDescription>> = {
  [CurrencyCode.AED]: currencyList[CurrencyCode.AED],
  [CurrencyCode.AUD]: currencyList[CurrencyCode.AUD],
  [CurrencyCode.BRL]: currencyList[CurrencyCode.BRL],
  [CurrencyCode.CAD]: currencyList[CurrencyCode.CAD],
  [CurrencyCode.CHF]: currencyList[CurrencyCode.CHF],
  [CurrencyCode.CNY]: currencyList[CurrencyCode.CNY],
  [CurrencyCode.CZK]: currencyList[CurrencyCode.CZK],
  [CurrencyCode.DKK]: currencyList[CurrencyCode.DKK],
  [CurrencyCode.EUR]: currencyList[CurrencyCode.EUR],
  [CurrencyCode.GBP]: currencyList[CurrencyCode.GBP],
  [CurrencyCode.HKD]: currencyList[CurrencyCode.HKD],
  [CurrencyCode.HUF]: currencyList[CurrencyCode.HUF],
  [CurrencyCode.ILS]: currencyList[CurrencyCode.ILS],
  [CurrencyCode.JPY]: currencyList[CurrencyCode.JPY],
  [CurrencyCode.KRW]: currencyList[CurrencyCode.KRW],
  [CurrencyCode.MXN]: currencyList[CurrencyCode.MXN],
  [CurrencyCode.NOK]: currencyList[CurrencyCode.NOK],
  [CurrencyCode.NZD]: currencyList[CurrencyCode.NZD],
  // [CurrencyCode.PHP]: currencyList[CurrencyCode.PHP], // Potentially not supported by Airwallex - fails
  [CurrencyCode.PLN]: currencyList[CurrencyCode.PLN],
  [CurrencyCode.RON]: currencyList[CurrencyCode.RON],
  [CurrencyCode.SEK]: currencyList[CurrencyCode.SEK],
  [CurrencyCode.SGD]: currencyList[CurrencyCode.SGD],
  [CurrencyCode.THB]: currencyList[CurrencyCode.THB],
  [CurrencyCode.USD]: currencyList[CurrencyCode.USD],
  [CurrencyCode.VND]: currencyList[CurrencyCode.VND],
  [CurrencyCode.ZAR]: currencyList[CurrencyCode.ZAR],
}

export const cardCurrencies: Partial<Record<CurrencyCode, CurrencyDescription>> = {
  [CurrencyCode.USD]: currencyList[CurrencyCode.USD],
  [CurrencyCode.EUR]: currencyList[CurrencyCode.EUR],
  [CurrencyCode.JPY]: currencyList[CurrencyCode.JPY],
  [CurrencyCode.GBP]: currencyList[CurrencyCode.GBP],
  [CurrencyCode.AUD]: currencyList[CurrencyCode.AUD],
  [CurrencyCode.HKD]: currencyList[CurrencyCode.HKD],
  [CurrencyCode.SGD]: currencyList[CurrencyCode.SGD],
  [CurrencyCode.CAD]: currencyList[CurrencyCode.CAD],
  [CurrencyCode.NZD]: currencyList[CurrencyCode.NZD],
  [CurrencyCode.CHF]: currencyList[CurrencyCode.CHF],
}

export const charterCurrencies: Partial<Record<CurrencyCode, CurrencyDescription>> = {
  [CurrencyCode.AED]: currencyList[CurrencyCode.AED],
  [CurrencyCode.AUD]: currencyList[CurrencyCode.AUD],
  [CurrencyCode.CAD]: currencyList[CurrencyCode.CAD],
  [CurrencyCode.CHF]: currencyList[CurrencyCode.CHF],
  [CurrencyCode.CNY]: currencyList[CurrencyCode.CNY],
  [CurrencyCode.CZK]: currencyList[CurrencyCode.CZK],
  [CurrencyCode.DKK]: currencyList[CurrencyCode.DKK],
  [CurrencyCode.EUR]: currencyList[CurrencyCode.EUR],
  [CurrencyCode.GBP]: currencyList[CurrencyCode.GBP],
  [CurrencyCode.HKD]: currencyList[CurrencyCode.HKD],
  [CurrencyCode.HUF]: currencyList[CurrencyCode.HUF],
  [CurrencyCode.ILS]: currencyList[CurrencyCode.ILS],
  [CurrencyCode.IDR]: currencyList[CurrencyCode.IDR],
  [CurrencyCode.JPY]: currencyList[CurrencyCode.JPY],
  [CurrencyCode.MXN]: currencyList[CurrencyCode.MXN],
  [CurrencyCode.NOK]: currencyList[CurrencyCode.NOK],
  [CurrencyCode.NZD]: currencyList[CurrencyCode.NZD],
  // [CurrencyCode.PHP]: currencyList[CurrencyCode.PHP], // Potentially not supported by Airwallex - fails
  [CurrencyCode.PLN]: currencyList[CurrencyCode.PLN],
  [CurrencyCode.RON]: currencyList[CurrencyCode.RON],
  [CurrencyCode.SEK]: currencyList[CurrencyCode.SEK],
  [CurrencyCode.SGD]: currencyList[CurrencyCode.SGD],
  [CurrencyCode.USD]: currencyList[CurrencyCode.USD],
  [CurrencyCode.ZAR]: currencyList[CurrencyCode.ZAR],
}
